import type { BasicSourceLogTypeDTO } from './SourceCreate.types';

/**
 * Source type identifiers mapped to backend database IDs.
 *
 * **Critical:** These IDs must match the `source_types` table in the database.
 * The backend uses these IDs for schema lookups, validation rules, and data routing.
 *
 * **Groupings:**
 *
 * **Cloud Identity & Access Management:**
 * - OKTA (20): Identity and SSO
 * - JUMP_CLOUD (47): Directory-as-a-Service
 * - ONE_PASSWORD (48): Password management
 *
 * **Microsoft Ecosystem:**
 * - O365 (4): Microsoft 365 logs (Exchange, SharePoint, Teams)
 * - MICROSOFT_DEFENDER (15): Defender for Endpoint, Cloud Apps
 * - MICROSOFT_ENTRA_ID (36): Azure Active Directory via Graph/O365 API
 * - MICROSOFT_SENTINEL (14): Azure Sentinel SIEM
 * - MICROSOFT (42): Unified Microsoft parent source (contains O365, Defender, Entra as providers)
 * - AZURE_EVENT_HUB (1): Azure Event Hubs streaming
 * - AZURE_VIRTUAL_NETWORK_FLOW_LOGS (31): Azure network traffic
 *
 * **Endpoint Detection & Response (EDR):**
 * - CARBON_BLACK (32): VMware Carbon Black
 * - CROWD_STRIKE (27): CrowdStrike Falcon
 * - CROWD_STRIKE_SIEM_CASES (49): CrowdStrike case management
 * - SENTINELONE (16): SentinelOne single-tenant
 * - SENTINELONE_MULTI_CUSTOMER (17): SentinelOne MSP multi-tenant
 * - SOPHOS_EDR (38): Sophos endpoint protection
 * - TRELLIX (18): Trellix EDR (formerly FireEye/McAfee)
 * - PALO_ALTO_CORTEX_XDR (44): Palo Alto Cortex
 * - CISCO_SECURE_ENDPOINT (33): Cisco AMP
 *
 * **Cloud Security & SIEM:**
 * - AWS_CLOUDTRAIL (19): AWS audit trail (legacy, replaced by AWS_S3)
 * - AWS_SECURITY_HUB (28): AWS Security Hub findings
 * - AWS_S3 (39): AWS S3 bucket ingestion (CloudTrail, GuardDuty, etc.)
 * - GOOGLE_STORAGE (40): Google Cloud Storage bucket ingestion
 * - GOOGLE_PUB_SUB (58): Google Pub/Sub
 * - GOOGLE_WORKSPACE (10): Google Workspace (Gmail, Drive, Admin)
 * - SNOWFLAKE (46): Snowflake data warehouse logs
 *
 * **Email Security:**
 * - MIMECAST (3): Email security and archiving
 * - PROOFPOINT_TAP (22): Targeted Attack Protection
 * - PROOFPOINT_POD (26): Email defense
 * - PROOFPOINT_ESSENTIALS (56): Proofpoint Essentials email security
 * - ABNORMAL_SECURITY (21): AI-powered email security
 *
 * **Network & Infrastructure:**
 * - CISCO_DUO (23): Duo MFA and access
 * - CISCO_MERAKI (37): Meraki wireless and network
 * - JUNIPER_MIST (30): Juniper wireless management
 * - CHECK_POINT (45): Check Point firewall
 * - THINKST_CANARY (41): Canary honeypot tokens
 *
 * **IT Management & Monitoring:**
 * - LOGMEIN_CENTRAL (34): LogMeIn remote management
 * - NINJAONE (53): NinjaOne RMM
 * - ACTION1 (43): Action1 RMM
 * - IT_GLUE (54): IT documentation and asset management
 * - ADDIGY (55): Apple device management (MDM/RMM)
 *
 * **Vulnerability & Compliance:**
 * - QUALYS (35): Qualys vulnerability scanning
 * - MEDIGATE (5): Medigate medical device security
 *
 * **Productivity & Collaboration:**
 * - SALESFORCE (6): Salesforce CRM audit logs
 * - DROPBOX (2): Dropbox file sharing
 * - NET_SUITE (29): NetSuite ERP
 * - GITHUB (52): GitHub repositories and actions
 * - MULESOFT (51): MuleSoft integration platform
 *
 * **Security Services:**
 * - THREAT_LOCKER (25): Application allow-listing
 * - IMPERVA_CLOUD_WAF (50): Imperva web application firewall
 *
 * **Generic/Legacy:**
 * - LOG_SOURCES (24): Generic syslog or custom log ingestion
 *
 * **ID Planning:**
 * IDs 1-62 are allocated. New sources should use ID 63+.
 */
enum SOURCE_TYPE {
  // Cloud Identity & Access Management
  OKTA = 20,
  JUMP_CLOUD = 47,
  ONE_PASSWORD = 48,

  // Microsoft Ecosystem
  O365 = 4,
  MICROSOFT_DEFENDER = 15,
  MICROSOFT_ENTRA_ID = 36,
  MICROSOFT_SENTINEL = 14,
  MICROSOFT = 42, // Parent source containing O365, Defender, Entra as data providers
  AZURE_EVENT_HUB = 1,
  AZURE_VIRTUAL_NETWORK_FLOW_LOGS = 31,

  // Endpoint Detection & Response (EDR)
  CARBON_BLACK = 32,
  CROWD_STRIKE = 27,
  CROWD_STRIKE_SIEM_CASES = 49,
  SENTINELONE = 16,
  SENTINELONE_MULTI_CUSTOMER = 17,
  SENTINELONE_MANAGED = 60,
  SENTINELONE_MANAGED_009 = 61,
  SOPHOS_EDR = 38,
  TRELLIX = 18,
  PALO_ALTO_CORTEX_XDR = 44,
  CISCO_SECURE_ENDPOINT = 33,

  // Cloud Security & SIEM
  AWS_CLOUDTRAIL = 19, // Legacy - use AWS_S3 for new deployments
  AWS_SECURITY_HUB = 28,
  AWS_S3 = 39, // Container-based ingestion (CloudTrail, GuardDuty, VPC, etc.)
  GOOGLE_STORAGE = 40, // Container-based ingestion
  GOOGLE_PUB_SUB = 58,
  GOOGLE_WORKSPACE = 10,
  SNOWFLAKE = 46,

  // Email Security
  MIMECAST = 3,
  PROOFPOINT_TAP = 22,
  PROOFPOINT_POD = 26,
  PROOFPOINT_ESSENTIALS = 56,
  ABNORMAL_SECURITY = 21,

  // Network & Infrastructure
  CISCO_DUO = 23,
  CISCO_MERAKI = 37,
  JUNIPER_MIST = 30,
  CHECK_POINT = 45,
  THINKST_CANARY = 41,

  // IT Management & Monitoring
  LOGMEIN_CENTRAL = 34,
  NINJAONE = 53,
  ACTION1 = 43,
  IT_GLUE = 54,
  ADDIGY = 55,

  // Vulnerability & Compliance
  QUALYS = 35,
  MEDIGATE = 5,

  // Productivity & Collaboration
  SALESFORCE = 6,
  DROPBOX = 2,
  NET_SUITE = 29,
  GITHUB = 52,
  MULESOFT = 51,

  // Security Services
  THREAT_LOCKER = 25,
  IMPERVA_CLOUD_WAF = 50,

  ACRONIS = 57, // Acronis Cyber Protect Cloud

  FILES_DOT_COM = 59, // Files.com cloud file storage

  RUBRIK = 62, // Rubrik Cloud Data Management

  // Generic/Legacy
  LOG_SOURCES = 24 // Generic syslog/custom logs
}

export const READ_ONLY_MANAGED_SOURCE_TYPES = new Set<number>([
  SOURCE_TYPE.SENTINELONE_MANAGED,
  SOURCE_TYPE.SENTINELONE_MANAGED_009
]);

export const isReadOnlyManagedSourceType = (
  sourceTypeId?: number | null
): boolean => {
  return sourceTypeId
    ? READ_ONLY_MANAGED_SOURCE_TYPES.has(sourceTypeId)
    : false;
};

export const MICROSOFT_CHILD_SOURCE_TYPES = new Set<number>([
  SOURCE_TYPE.O365,
  SOURCE_TYPE.MICROSOFT_DEFENDER,
  SOURCE_TYPE.MICROSOFT_ENTRA_ID
]);

export const isMicrosoftChildSourceType = (
  sourceTypeId?: number | null
): boolean => {
  return sourceTypeId ? MICROSOFT_CHILD_SOURCE_TYPES.has(sourceTypeId) : false;
};

export const LOG_SOURCE_TYPES_WITH_INFO_BLOCK = [
  SOURCE_TYPE.AZURE_EVENT_HUB,
  SOURCE_TYPE.GOOGLE_PUB_SUB
] as const;

// ============================================================================
// Source types that support category selection in the form
// ============================================================================
export const CATEGORY_SUPPORTED_SOURCE_TYPES = [
  SOURCE_TYPE.MULESOFT,
  SOURCE_TYPE.SNOWFLAKE,
  SOURCE_TYPE.DROPBOX,
  SOURCE_TYPE.PROOFPOINT_TAP,
  SOURCE_TYPE.LOGMEIN_CENTRAL,
  SOURCE_TYPE.MIMECAST,
  SOURCE_TYPE.MEDIGATE,
  SOURCE_TYPE.SENTINELONE,
  SOURCE_TYPE.JUNIPER_MIST,
  SOURCE_TYPE.SOPHOS_EDR,
  SOURCE_TYPE.ACRONIS,
  SOURCE_TYPE.GOOGLE_WORKSPACE,
  SOURCE_TYPE.CROWD_STRIKE,
  SOURCE_TYPE.JUMP_CLOUD,
  SOURCE_TYPE.PROOFPOINT_ESSENTIALS,
  SOURCE_TYPE.PALO_ALTO_CORTEX_XDR,
  SOURCE_TYPE.ONE_PASSWORD,
  SOURCE_TYPE.AWS_SECURITY_HUB,
  SOURCE_TYPE.CISCO_MERAKI,
  SOURCE_TYPE.SALESFORCE,
  SOURCE_TYPE.CHECK_POINT,
  SOURCE_TYPE.CISCO_DUO,
  SOURCE_TYPE.PROOFPOINT_POD,
  SOURCE_TYPE.FILES_DOT_COM,
  SOURCE_TYPE.RUBRIK
];

export const SOURCE_TYPES_SUPPORTING_FILE_UPLOADS = [
  SOURCE_TYPE.GOOGLE_WORKSPACE,
  SOURCE_TYPE.OKTA,
  SOURCE_TYPE.GOOGLE_PUB_SUB,
  SOURCE_TYPE.GOOGLE_STORAGE,
  SOURCE_TYPE.SNOWFLAKE
];

/**
 * Source types that display an additional information/disclosure block in the UI.
 * Typically used for sources requiring setup instructions, prerequisites, or supported log categories.
 * Includes both log aggregation sources (Event Hubs, Pub/Sub) and EDR products (CrowdStrike)
 * that expose log event categories for selective ingestion.
 */
export const SOURCE_TYPES_WITH_INFO_BLOCK = [
  SOURCE_TYPE.AZURE_EVENT_HUB,
  SOURCE_TYPE.GOOGLE_PUB_SUB,
  SOURCE_TYPE.CROWD_STRIKE
] as const;

/**
 * Confirmation dialog configuration for AWS S3 data providers without prefixes.
 *
 * **Business Context:**
 * AWS S3 data providers (CloudTrail, GuardDuty, Cisco Umbrella, DNS Filter) typically
 * organize logs in predictable directory structures (e.g., `/AWSLogs/123456/CloudTrail/`).
 * When users don't specify a prefix, the system assumes the default AWS structure.
 *
 * **Why confirmation is needed:**
 * - Without a prefix, the system scans the entire bucket (expensive and slow)
 * - Default paths may not match the customer's actual structure
 * - Incorrect paths lead to ingestion failures that are hard to diagnose
 *
 * **User flow:**
 * 1. User leaves prefix field empty for a data provider
 * 2. Dialog shows: "When a prefix is not specified, the system assumes [these paths]"
 * 3. User confirms or goes back to specify correct prefix
 *
 * **Configuration structure:**
 * - `commonPrefixText`: First paragraph explaining default behavior
 * - `commonSuffixText`: Question asking for confirmation
 * - `providers`: Per-provider default path examples with titles
 */
export const prefixMsgConfig = {
  commonPrefixText:
    'When a prefix is not specified, the system assumes that logs are stored in the default directory structure.',

  commonSuffixText: 'Do you want to continue with the default paths?',

  providers: {
    // Cisco Umbrella DNS, proxy, firewall, and IPS logs
    cisco: {
      title: 'Cisco Umbrella',
      paths: [
        '/auditlogs/<YYYY-MM-DD>/...',
        '/proxylogs/<YYYY-MM-DD>/...',
        '/dnslogs/<YYYY-MM-DD>/...',
        '/firewalllogs/<YYYY-MM-DD>/...',
        '/intrusionlogs/<YYYY-MM-DD>/...',
        '/iplogs/<YYYY-MM-DD>/...'
      ]
    },
    // AWS GuardDuty findings stored in standard AWS log structure
    guardduty: {
      title: 'AWS GuardDuty',
      paths: ['/AWSLogs/...']
    },
    // DNS Filter cloud DNS security logs
    dnsfilter: {
      title: 'DNS Filter',
      paths: ['/dnsfilter/<YYYY-MM-DD>/...']
    },
    // AWS CloudTrail API activity logs in standard structure
    awscloudtrail: {
      title: 'AWS Cloudtrail',
      paths: ['/AWSLogs/...']
    }
  }
};

/**
 * Microsoft data provider names (not full form paths).
 *
 * **Usage:**
 * Used for provider identification in Edit mode to determine whether a provider
 * already exists in the database vs is being newly added.
 *
 * **Corresponds to:**
 * - GraphAPI: Microsoft Entra ID via Microsoft Graph API
 * - O365API: Microsoft Entra ID via Office 365 Management API
 * - MicrosoftDefenderUnified: Microsoft Defender (Cloud Apps, Endpoint, etc.)
 * - O365: Microsoft 365 (Exchange, SharePoint, Teams, OneDrive)
 */
export enum SubProvider {
  GraphAPI = 'GraphAPI',
  O365API = 'O365API',
  MicrosoftDefenderUnified = 'MicrosoftDefenderUnified',
  O365 = 'O365'
}

/**
 * Full React Hook Form paths for Microsoft Entra ID data providers.
 *
 * **Why separate from SubProvider:**
 * SubProvider stores just the name (e.g., "GraphAPI") for backend payloads.
 * DataProviderPath stores the full form field path (e.g., "data_provider.MicrosoftEntraID.GraphAPI")
 * needed for React Hook Form registration and value access.
 *
 * **Form structure:**
 * ```
 * data_provider: {
 *   MicrosoftEntraID: {
 *     GraphAPI: { site_name, ingest_from, ... },
 *     O365API: { site_name, ingest_from, ... }
 *   },
 *   MicrosoftDefenderUnified: { site_name, category, ... },
 *   O365: { site_name, category, ... }
 * }
 * ```
 *
 * **Note:** O365 path is not included because it's not nested under MicrosoftEntraID.
 */
export enum DataProviderPath {
  GraphAPI = 'data_provider.MicrosoftEntraID.GraphAPI',
  O365API = 'data_provider.MicrosoftEntraID.O365API',
  MicrosoftDefenderUnified = 'data_provider.MicrosoftDefenderUnified'
}

/**
 * Special category names for Microsoft Defender conditional logic.
 *
 * **Business rules:**
 *
 * **AADSignin (AAD Identity Protection - SignIn):**
 * - When selected, enables "Enterprise Plan" subscription type option
 * - Contains sign-in risk events and identity protection logs
 *
 * **CloudApps (Defender for Cloud Apps):**
 * - When selected, makes `api_url` field mandatory
 * - Requires customer's specific Defender for Cloud Apps URL
 * - URL format: `https://<tenant>.portal.cloudappsecurity.com`
 *
 * **Why hardcoded:**
 * These exact category names (including casing and punctuation) come from the
 * backend database and are used in conditional validation logic throughout the form.
 */
export enum DefenderCategory {
  AADSignin = 'aad identity protection - signin',
  CloudApps = 'Defender for Cloud Apps'
}

/**
 * Form field names for Microsoft data provider configuration.
 *
 * **Field purposes:**
 * - `SubscriptionType`: Microsoft license level (E3, E5, Business, Enterprise Plan)
 * - `DataproviderCategory`: Array of selected log categories for the provider
 * - `ApiUrl`: Defender for Cloud Apps tenant-specific API endpoint
 *
 * **Usage:**
 * Used with React Hook Form's `useWatch()` and `setValue()` to construct full field paths:
 * `${providerPath}.${DataProviderField.SubscriptionType}`
 */
export enum DataProviderField {
  SubscriptionType = 'subscription_type',
  DataproviderCategory = 'dataprovider_category',
  ApiUrl = 'api_url'
}

/**
 * Keyword for detecting sign-in related categories.
 *
 * **Usage:**
 * Case-insensitive search to identify sign-in category selections.
 * When a category contains "signin", special subscription type filtering applies.
 */
export const SIGNIN_KEYWORD = 'signin';

// ============================================================================
// Category Predicate Functions
// ============================================================================
// These predicates encapsulate business rules for category filtering.
// Each function is testable in isolation and documents specific requirements.
// ============================================================================

/** Checks if category name contains sign-in related keywords */
export const isSigninCategory = (name: string): boolean =>
  name.toLowerCase().includes(SIGNIN_KEYWORD);

/**
 * Checks if category is AAD Sign-in (Defender exclusive).
 * Business Rule: AAD Sign-in requires Microsoft Defender Unified due to licensing.
 */
export const isAADSigninCategory = (name: string): boolean =>
  name.toLowerCase() === DefenderCategory.AADSignin;

/**
 * Checks if category is Azure Directory Audit (Graph API exclusive).
 * Business Rule: Azure AD Audit must use Graph API instead of legacy O365 API.
 */
export const isAzureDirectoryAuditCategory = (name: string): boolean =>
  name.toLowerCase() === 'audit.azureactivedirectory';

/** Checks if category name matches the specified suffix filter */
export const matchesSuffix = (name: string, suffix?: string): boolean =>
  !suffix || name.toLowerCase().endsWith(suffix.toLowerCase());

/**
 * Default category selection strategies by Microsoft data provider.
 *
 * **Business Rules by Provider:**
 * - **O365 (legacy):** All categories EXCEPT Azure AD Audit (requires Graph API)
 * - **Defender:** All categories EXCEPT AAD Sign-in (licensing restriction)
 * - **Graph API:** Categories matching suffix EXCEPT sign-in (requires enterprise license)
 * - **O365 API:** All categories matching suffix
 *
 * **Usage:**
 * Used in `useCategoriesBySourceType` hook to pre-select appropriate categories
 * when creating a new Microsoft source or adding a new data provider.
 *
 * **Edit Mode Behavior:**
 * If the provider already exists on the source, these defaults are NOT applied
 * to preserve the user's existing selection.
 */
export const DEFAULT_CATEGORY_STRATEGIES = {
  // Exclude Azure AD Audit (must use Graph API instead)
  [SubProvider.O365]: (cat: BasicSourceLogTypeDTO) =>
    !isAzureDirectoryAuditCategory(cat.name),

  // Exclude AAD Sign-in (licensing limitation)
  [SubProvider.MicrosoftDefenderUnified]: (cat: BasicSourceLogTypeDTO) =>
    !isAADSigninCategory(cat.name),

  // Include suffix matches but exclude sign-in (enterprise license required)
  [SubProvider.GraphAPI]: (cat: BasicSourceLogTypeDTO, suffix?: string) =>
    matchesSuffix(cat.name, suffix) && !isSigninCategory(cat.name),

  // Include all suffix matches
  [SubProvider.O365API]: (cat: BasicSourceLogTypeDTO, suffix?: string) =>
    matchesSuffix(cat.name, suffix)
} as const;

/**
 * CrowdStrike-specific categories that should be filtered out from the main
 * category list. These are Cloud Security Posture Management (CSPM) categories
 * that are handled separately.
 */
export const CROWDSTRIKE_CSPM_CATEGORIES = [
  'CSPMIOAStreamingEvent-aws',
  'CSPMIOAStreamingEvent-azure'
] as const;

/**
 * Core form fields that should never be unregistered during source type transitions.
 * These fields maintain form continuity across source type changes.
 */
export const CORE_FORM_FIELDS = [
  'source_type_id',
  'device_uid',
  'org_id',
  'organization_name',
  'device_name',
  'ip_address'
] as const;

/**
 * Default sub-source type values for sources that require selection.
 *
 * Business rules:
 * - Mimecast: Version2 (API v2 is preferred over legacy v1)
 * - Cisco Secure Endpoint: Yes (new SQS queue setup vs existing queue)
 * - Qualys: Session (session-based auth vs basic auth)
 */
export const DEFAULT_SUB_SOURCE_TYPES: Record<number, string> = {
  [SOURCE_TYPE.MIMECAST]: 'Version2',
  [SOURCE_TYPE.CISCO_SECURE_ENDPOINT]: 'Yes',
  [SOURCE_TYPE.QUALYS]: 'Session'
};

/**
 * Core form fields that should never be included in connection_inputs.
 * These fields are root-level properties of the source payload.
 */
export const ROOT_LEVEL_ATTRIBUTES = [
  'device_uid',
  'source_name',
  'site_name',
  'source_type_id',
  'organization_id',
  'organization_name',
  'org_id',
  'source_type_name',
  'ip_address',
  'active',
  'device_name',
  'service_detail_name',
  'public_key',
  'private_key',
  'newly_added_category',
  'newly_added_cloud_provider',
  'device_alert_fields',
  'edit_status',
  'is_queue_name_changed',
  'data_provider_selection',
  'data_provider',
  'file_upload',
  'cert_name',
  'host_name',
  'encrypt_alg',
  'org_unit',
  'org',
  'location',
  'country',
  'state',
  'email'
] as const;

/**
 * Subscription type keyword for Enterprise Plan option.
 *
 * **Business rule:**
 * "Enterprise Plan" subscription type is only available when AAD SignIn category
 * is selected for Microsoft Defender.
 */
export const ENTERPRISE_PLAN_KEYWORD = 'enterprise plan';

/**
 * Validation error message for missing Defender Cloud Apps API URL.
 *
 * **Trigger:** User selects "Defender for Cloud Apps" category but leaves api_url empty.
 *
 * **Resolution:** User must provide their tenant-specific Cloud Apps URL.
 */
export const API_URL_REQUIRED_MESSAGE =
  'The API URL is mandatory when the "Defender for Cloud Apps" category is selected.';

/**
 * Type definition for source information block metadata.
 *
 * **Purpose:**
 * Controls the content of the information panel shown below certain source type forms.
 *
 * **Fields:**
 * - `source_type_id`: Which source type this config applies to
 * - `sub_source`: Optional sub-provider (e.g., "GraphAPI" vs "O365API" for Entra ID)
 * - `title`: Header text for the info block
 * - `footer_note`: Explanatory text or limitation notice
 */
export type UiMetaDataConfig = {
  source_type_id: SOURCE_TYPE;
  sub_source: string;
  title: string;
  footer_note: string;
};

/**
 * Configuration for source-specific information blocks.
 *
 * **Use cases:**
 *
 * **Azure Event Hub:**
 * Shows list of supported data providers that get normalized.
 * Footer notes that other sources are ingested as generic "event_hub" type.
 *
 * **Microsoft Entra ID (GraphAPI):**
 * Warns that only interactive sign-in logs are available via Graph API.
 * Users needing full sign-in coverage should use O365API.
 *
 * **Microsoft Entra ID (O365API):**
 * Confirms that all sign-in log types are available:
 * - Interactive sign-in
 * - Non-interactive sign-in (service accounts, automated tasks)
 * - Service principal sign-in (application authentication)
 * - Managed identity sign-in (Azure resource authentication)
 *
 * **Extension pattern:**
 * Add new entries when sources need to display warnings, limitations, or
 * special configuration notes to users.
 */
export const UI_META_DATA_CONFIG: UiMetaDataConfig[] = [
  {
    source_type_id: SOURCE_TYPE.AZURE_EVENT_HUB,
    sub_source: '',
    title: 'Supported data providers for data normalization and monitoring*',
    footer_note:
      '* Other log sources are ingested in raw format as a generic "event_hub" data source.'
  },
  {
    source_type_id: SOURCE_TYPE.MICROSOFT_ENTRA_ID,
    sub_source: 'GraphAPI',
    title: '',
    footer_note: 'Limited sign-in logs available: Interactive sign-in'
  },
  {
    source_type_id: SOURCE_TYPE.MICROSOFT_ENTRA_ID,
    sub_source: 'O365API',
    title: '',
    footer_note:
      'All sign-in logs available: Interactive sign-in, Non-interactive sign-in, Service principal sign-in, Managed identity sign-in'
  },
  {
    source_type_id: SOURCE_TYPE.GOOGLE_PUB_SUB,
    sub_source: '',
    title: 'Supported data providers for data normalization and monitoring*',
    footer_note:
      '* Other log sources are ingested in raw format as a generic "google_pub_sub" data source.'
  },
  {
    source_type_id: SOURCE_TYPE.CROWD_STRIKE,
    sub_source: '',
    title: 'Supported data providers for data normalization and monitoring*',
    footer_note:
      '* Other log sources are ingested in raw format as a generic "crowdstrike_falcon" data source.'
  }
];

/**
 * Step identifiers for the multi-step source creation wizard.
 *
 * **Wizard flow:**
 * 1. **SELECT_SOURCE (Step 1):** Visual source type picker with vendor logos
 * 2. **CONFIGURE_SOURCE (Step 2):** Dynamic form fields based on selected type
 *
 * **Note:** Uses numeric values (1, 2) instead of 0-based indexing for user-facing step numbers.
 */
export const WIZARD_STEP = {
  SELECT_SOURCE: 1,
  CONFIGURE_SOURCE: 2
} as const;

export default SOURCE_TYPE;
